// datetimeUtils.ts

// datetimeUtils.ts
import moment, { Moment } from "moment-timezone";

/** The timezone your app runs in production (Toronto/Canada) */
export const TIMEZONE = "America/Toronto";

/**
 * Convert a UTC datetime string (from backend) into a Date object
 * that visually matches the local time for the provided timezone (ignores device timezone).
 * This is the generic version. Pass a timezone string (IANA) or omit to use the app default.
 */
export const utcToTimezoneDate = (utcString?: string, tz: string = TIMEZONE): Date | null => {
  if (!utcString) return null;

  const m: Moment = moment.utc(utcString).tz(tz);

  // Construct a Date manually — prevents shifting to device timezone
  return new Date(
    m.year(),
    m.month(),
    m.date(),
    m.hour(),
    m.minute(),
    m.second()
  );
};

/**
 * Convert a local Date (representing wall time in the given timezone)
 * back to an ISO UTC string to send to backend.
 */
export const timezoneDateToUtc = (date?: Date, tz: string = TIMEZONE): string | null => {
  if (!date) return null;
  return moment.tz(moment(date), tz).utc().format();
};

// Backwards-compatible wrappers (so existing callers keep working)
export const utcToTorontoDate = (utcString?: string): Date | null => utcToTimezoneDate(utcString, TIMEZONE);
export const torontoDateToUtc = (date?: Date): string | null => timezoneDateToUtc(date, TIMEZONE);

/**
 * Serialize a date-only selection (calendar day) to an ISO string at UTC midnight.
 *
 * This avoids timezone-induced day shifting when a UI date picker (date-only)
 * is persisted as a timestamp.
 */
export const dateOnlyToUtcIso = (date?: Date | null): string | null => {
  if (!date) return null;
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
};

/**
 * Calculate absolute difference in hours between two moment instances.
 */
export const hoursDiff = (a: moment.Moment, b: moment.Moment) => Math.abs(a.diff(b, "hours", true));

// export const hoursDiff = (a: Date | string, b: Date | string): number => {
//   return Math.abs(moment(a).diff(moment(b), "hours", true));
// };
//   return Math.abs(moment(a).diff(moment(b), "hours", true));
